import React from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { Bell, MessageSquare, User, CheckCheck } from "lucide-react";

export default function Notifications() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["comNotifs"], queryFn: async () => (await api.get("/community/notifications")).data, enabled: !!user });

  const markRead = async () => { await api.post("/community/notifications/read"); qc.invalidateQueries({ queryKey: ["comNotifs"] }); };

  const label = (n) => {
    const map = { reply: "com_notif_reply", followed_reply: "com_notif_followed_reply", mention: "com_notif_mention", moderation_warn: "com_notif_warn" };
    return t(map[n.type] || "com_notif_reply");
  };

  if (!user) return <div className="py-24 text-center text-stone-400">{t("com_login_required")}</div>;

  return (
    <>
      <Seo title={t("nav_notifications")} canonicalPath="/notifications" noindex />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="flex items-center justify-between mb-6 border-b border-amber-900/30 pb-4">
          <h1 className="font-serif text-3xl text-amber-100 flex items-center gap-2"><Bell className="w-6 h-6 text-amber-500" /> {t("nav_notifications")}</h1>
          {(data?.unread || 0) > 0 && <button data-testid="notif-mark-read" onClick={markRead} className="inline-flex items-center gap-1.5 text-sm text-amber-500 hover:text-amber-400"><CheckCheck className="w-4 h-4" /> {t("com_notif_mark_read")}</button>}
        </div>
        {!data?.notifications?.length ? (
          <div className="text-center py-16 text-stone-500 border border-dashed border-[#272A34] rounded-lg" data-testid="notif-empty">{t("com_notif_none")}</div>
        ) : (
          <div className="space-y-2">
            {data.notifications.map((n) => (
              <Link key={n.id} to={n.post_id ? `/community/post/${n.post_id}` : "/community"} data-testid={`notif-${n.id}`}
                className={`flex items-start gap-3 rounded-lg p-3 border transition-all ${n.read ? "bg-[#121418] border-[#272A34]" : "bg-amber-950/20 border-amber-800/40"}`}>
                <span className="w-9 h-9 rounded-full bg-amber-900/40 flex items-center justify-center shrink-0"><MessageSquare className="w-4 h-4 text-amber-400" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-200"><span className="font-medium">{n.actor_name || t("com_official")}</span> {label(n)}</p>
                  <span className="font-mono text-[10px] text-stone-500">{formatDate(n.created_at, lang)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
